import React from 'react';
import {
    Overflow,
    OverflowItem,
    Tooltip,
    Button,
    useIsOverflowItemVisible,
} from '@fluentui/react-components';
import { isCapabilityEnabled } from 'owa-capabilities';
import { headerFullBleedCapability } from 'owa-capabilities-definitions/lib/headerFullBleedCapability';
import classNames from 'owa-classnames';
import { isDrawerPaneVisible } from 'owa-drawer';
import isWin32StylingEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';
import { HEADER_BUTTONS_REGION_ID } from 'owa-header-constants';
import type { OwaHeaderButtonControl, OwaHeaderButtonControls } from 'owa-header-types';
import { observer } from 'owa-mobx-react';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import useButtonGroupContainerStyle from '../../hooks/useButtonGroupContainerStyle';
import useHeaderButtonVisibility from '../../hooks/useHeaderButtonVisibility';
import toggleHeaderPane from '../../orchestration/toggleHeaderPane';
import logHeaderButtonClick from '../../utils/logHeaderButtonClick';
import { BUTTON_WIDTH } from '../../constants/constants';
import OwaHeaderButtonBadge from './OwaHeaderButtonBadge';
import OwaHeaderOverflowMenu from './OwaHeaderOverflowMenu';
import {
    activeButton,
    activeFilledOnHoverButton,
    buttonBadge,
    buttonFullBleed,
    filledOnHoverButton,
    headerButtonFlat,
    buttonIcon,
    headerButton,
    rewardsHeaderButton,
    headerButtonGroup,
    headerButtonGroupFlat,
    overflowContainer,
    overflowContainerFlat,
    buttonLabel,
    wideContentButton,
    wideContentIcon,
} from './OwaHeaderButtonGroup.scss';

interface OwaHeaderButtonGroupProps {
    buttons: OwaHeaderButtonControls;
    regionId?: string;
}

interface OverflowAwareHeaderButtonProps {
    id: string;
    priority: number;
    onShow: () => void;
    children: React.ReactElement;
}

const defaultExport: React.FunctionComponent<OwaHeaderButtonGroupProps> = observer(
    function OwaHeaderButtonGroup({ buttons, regionId = HEADER_BUTTONS_REGION_ID }) {
        // Account-scoped buttons (e.g. the consumer-only Rewards button) read the module-context
        // mailbox inside their `isVisible()`. Because this component is an `observer` and
        // useHeaderButtonVisibility evaluates visibility via a MobX `computed`, those buttons toggle
        // on an in-app account switch without a reload — no explicit account dependency needed.
        const visibleButtons = useHeaderButtonVisibility(buttons);
        const reservedSlotButtons = React.useMemo(
            () => visibleButtons.filter(button => button.overflowItem?.addsHeaderSlot),
            [visibleButtons]
        );
        const reservedExtraWidth = useComputedValue(
            () =>
                reservedSlotButtons.reduce((sum, button) => {
                    const resolved =
                        typeof button.contentWidth === 'function'
                            ? button.contentWidth()
                            : button.contentWidth;
                    return sum + Math.max(0, (resolved ?? BUTTON_WIDTH) - BUTTON_WIDTH);
                }, 0),
            [reservedSlotButtons]
        );
        const containerStyle = useButtonGroupContainerStyle(
            visibleButtons.length,
            reservedSlotButtons.length,
            reservedExtraWidth
        );

        if (isWin32StylingEnabled()) {
            return (
                <div className={headerButtonGroupFlat}>
                    <div id={regionId} className={overflowContainerFlat}>
                        {visibleButtons.map(button => (
                            <OwaHeaderButton key={button.id} flat={true} {...button} />
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <div style={containerStyle} className={headerButtonGroup}>
                <Overflow overflowDirection="start">
                    <div id={regionId} className={overflowContainer}>
                        {visibleButtons.map(button => (
                            <OwaHeaderButton key={button.id} {...button} />
                        ))}
                        <OwaHeaderOverflowMenu buttons={visibleButtons} />
                    </div>
                </Overflow>
            </div>
        );
    },
    'OwaHeaderButtonGroup'
);

const OverflowAwareHeaderButton: React.FC<OverflowAwareHeaderButtonProps> = ({
    id,
    priority,
    onShow,
    children,
}) => {
    const isVisible = useIsOverflowItemVisible(id);

    React.useEffect(() => {
        if (isVisible) {
            onShow();
        }
    }, [isVisible, onShow]);

    return (
        <OverflowItem id={id} priority={priority}>
            {children}
        </OverflowItem>
    );
};

export const OwaHeaderButton: React.FC<
    OwaHeaderButtonControl & {
        flat?: boolean;
    }
> = observer(
    ({
        id,
        renderLabel,
        renderIcon,
        renderTooltip,
        showLabel,
        onClick,
        onShow,
        onHover,
        pane,
        badge,
        overflowItem,
        contentWidth,
        flat,
    }) => {
        React.useEffect(() => {
            if (flat) {
                onShow?.();
            }
        }, [flat, onShow]);

        const handleClick = React.useCallback(() => {
            logHeaderButtonClick(id, 'header');
            onClick?.();
            if (pane) {
                toggleHeaderPane(pane.id);
            }
        }, [id, pane, onClick]);

        const isActive = useComputedValue(() => {
            return pane ? isDrawerPaneVisible(pane.id) : false;
        }, [pane?.id]);

        const icon = useComputedValue(() => {
            return (
                <div className={classNames(buttonIcon, contentWidth != null && wideContentIcon)}>
                    {renderIcon('header')}
                    {badge?.getCount && (
                        <OwaHeaderButtonBadge
                            id={id}
                            getCount={badge.getCount}
                            className={buttonBadge}
                        />
                    )}
                </div>
            );
        }, [renderIcon, id, badge?.getCount, contentWidth]);

        const tooltip = renderTooltip ? renderTooltip() : renderLabel();
        // When the label is shown inline, use it as the accessible name so assistive technology
        // announces the same text that's visible (WCAG 2.5.3 Label in Name); otherwise fall back to
        // the tooltip for icon-only buttons.
        const accessibleName = showLabel ? renderLabel() : tooltip;
        const buttonStyle = useComputedValue(() => {
            const resolved = typeof contentWidth === 'function' ? contentWidth() : contentWidth;
            if (resolved == null) {
                return undefined;
            }
            // Labeled buttons (e.g. Buy Microsoft 365) size to their content and treat
            // `contentWidth` as a maximum, so localized labels of differing lengths render without a
            // per-locale width calculation and truncate with an ellipsis when they exceed the cap.
            // Icon/widget buttons (e.g. Rewards) use the resolved width so their glyph stays centered.
            return showLabel ? { maxWidth: resolved } : { width: resolved, minWidth: resolved };
        }, [contentWidth, showLabel]);

        const priority = overflowItem?.priority ?? 0;

        const button = (
            <Tooltip content={tooltip} relationship="description">
                <Button
                    id={id}
                    aria-label={accessibleName}
                    aria-expanded={pane ? isActive : undefined}
                    className={classNames(
                        headerButton,
                        id === 'owaRewardsBtn_container' && rewardsHeaderButton,
                        flat && headerButtonFlat,
                        isCapabilityEnabled(headerFullBleedCapability) && buttonFullBleed,
                        filledOnHoverButton,
                        isActive && activeFilledOnHoverButton,
                        isActive && activeButton,
                        contentWidth != null && wideContentButton
                    )}
                    style={buttonStyle}
                    icon={icon}
                    onClick={handleClick}
                    onMouseEnter={onHover}
                >
                    {showLabel ? <span className={buttonLabel}>{renderLabel()}</span> : undefined}
                </Button>
            </Tooltip>
        );

        return (
            <>
                {flat ? (
                    button
                ) : onShow ? (
                    <OverflowAwareHeaderButton id={id} priority={priority} onShow={onShow}>
                        {button}
                    </OverflowAwareHeaderButton>
                ) : (
                    <OverflowItem key={id} id={id} priority={priority}>
                        {button}
                    </OverflowItem>
                )}
                {pane?.render && pane.render(pane.id)}
            </>
        );
    },
    'OwaHeaderButton'
);

export default defaultExport;
